import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, IconButton, Divider, Chip, Fab, Badge, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PlaceIcon from '@mui/icons-material/Place';
import PersonIcon from '@mui/icons-material/Person';
import axios from 'axios';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getHariFromTanggal, parseJamMulai } from '../utils/dateUtils';

type Item = {
  tanggal: string;
  tempat: string;
  nama: string;
  level?: string;
  jamMulai?: string | null;
  jamRaw?: string | null;
  idBooking?: string;
  orderId?: string;
  type: 'mabar' | 'coaching';
};

const FloatingBookingStatus: React.FC = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [visible, setVisible] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<Item | null>(null);
  const [pendingPayments, setPendingPayments] = useState<{ orderId: string; redirectUrl: string; createdAt: number }[]>([]);

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);

  const loadPendingPayments = () => {
    const payments: { orderId: string; redirectUrl: string; createdAt: number }[] = [];
    for (const key of Object.keys(localStorage)) {
      if (!key.startsWith('payment_')) continue;
      const stored = localStorage.getItem(key);
      if (!stored) continue;
      try {
        const data = JSON.parse(stored);
        const orderId = key.replace('payment_', '');
        const age = Date.now() - (data.createdAt || 0);
        if (data.redirectUrl && age < 24 * 60 * 60 * 1000) {
          payments.push({ orderId, redirectUrl: data.redirectUrl, createdAt: data.createdAt || 0 });
        }
      } catch {
        continue;
      }
    }
    setPendingPayments(payments);
  };

  useEffect(() => {
    if (sessionStorage.getItem('bookingStatusHidden') === '1') {
      setVisible(false);
    }
    loadPendingPayments();
  }, []);

  // Helper parse
  const parseIndoDate = (tanggal: string, jam?: string | null): Date | null => {
    try {
      if (!tanggal) return null;
      const [d, bulan, y] = tanggal.split(' ');
      const bulanMap: any = { 'Januari':0,'Februari':1,'Maret':2,'April':3,'Mei':4,'Juni':5,'Juli':6,'Agustus':7,'September':8,'Oktober':9,'November':10,'Desember':11 };
      let hh = 0, mm = 0;
      if (jam) {
        const parts = jam.includes(':') ? jam.split(':') : jam.split('.');
        hh = parseInt(parts[0] || '0', 10);
        mm = parseInt(parts[1] || '0', 10);
      }
      return new Date(parseInt(y,10), bulanMap[bulan], parseInt(d,10), hh, mm, 0, 0);
    } catch {
      return null;
    }
  };

  const loadData = async () => {
    const email = (user?.email || '').toString();
    if (!email) return;
    try {
      const [mabarRes, coachingRes] = await Promise.all([
        axios.get<Item[]>(`/api/bookings?action=my-upcoming&email=${encodeURIComponent(email)}`),
        axios.get<Item[]>(`/api/coaching?action=my-upcoming&email=${encodeURIComponent(email)}`)
      ]);
      const list = [...(mabarRes.data || []), ...(coachingRes.data || [])];
      const norm = list.map(it => ({
        ...it,
        jamMulai: it.jamMulai ?? parseJamMulai(it.jamRaw || undefined)
      }));
      // Default jam 23:59 jika jam tidak ada, agar booking hari ini tetap tampil
      const future = norm.filter(it => {
        const jam = it.jamMulai && it.jamMulai.trim() ? it.jamMulai : '23:59';
        const d = parseIndoDate(it.tanggal, jam);
        return !!d && d.getTime() >= Date.now();
      });
      setItems(future);
    } catch (e) {
      setItems([]);
    }
  };

  useEffect(() => {
    const load = async () => {
      await loadData();
      loadPendingPayments();
    };
    load();
    const handler = () => {
      loadData();
      loadPendingPayments();
    };
    window.addEventListener('booking-updated', handler);
    return () => {
      window.removeEventListener('booking-updated', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  if (!visible || !user?.email) return null;

  const count = items.length;

  const canCancel = (it: Item): boolean => {
    const start = parseIndoDate(it.tanggal, (it.jamMulai && it.jamMulai.trim()) ? it.jamMulai : '23:59');
    if (!start) return false;
    const now = new Date();
    const diffMs = start.getTime() - now.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours >= 3;
  };

  const hasPendingPayment = (it: Item): boolean => {
    if (!it.orderId) return false;
    const paymentData = localStorage.getItem(`payment_${it.orderId}`);
    if (!paymentData) return false;
    try {
      const data = JSON.parse(paymentData);
      // Cek apakah belum expired (24 jam)
      const age = Date.now() - (data.createdAt || 0);
      return age < 24 * 60 * 60 * 1000;
    } catch {
      return false;
    }
  };

  const handlePaymentClick = (it: Item) => {
    if (!it.orderId) return;
    const paymentData = localStorage.getItem(`payment_${it.orderId}`);
    if (paymentData) {
      try {
        const data = JSON.parse(paymentData);
        window.location.href = data.redirectUrl;
      } catch (e) {
        console.error('Invalid payment data:', e);
      }
    }
  };

  return (
    <Box sx={{ position: 'fixed', left: '50%', bottom: 16, transform: 'translateX(-50%)', zIndex: 1300, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      {!expanded ? (
        <Fab color="primary" variant="extended" onClick={() => setExpanded(true)} sx={{ boxShadow: 6 }}>
          <Badge color="secondary" badgeContent={count} overlap="circular" sx={{ mr: 1 }}>
            <ListAltIcon />
          </Badge>
          Booking Aktif
          <ExpandMoreIcon sx={{ ml: 0.5 }} />
        </Fab>
      ) : (
        <Paper elevation={8} sx={{ p: 2, borderRadius: 3, minWidth: 320, maxWidth: 420, background: theme => theme.palette.mode === 'dark' ? 'rgba(18,18,18,0.98)' : 'rgba(255,255,255,0.98)', border: theme => `1px solid ${theme.palette.mode==='dark'?'rgba(255,255,255,0.12)':'rgba(0,0,0,0.08)'}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Status Booking Kamu ({count})
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton size="small" onClick={() => setExpanded(false)} title="Minimize">
                <ExpandLessIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => { setVisible(false); sessionStorage.setItem('bookingStatusHidden','1'); }} title="Tutup">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Divider sx={{ mb: 1 }} />
          {pendingPayments.length > 0 && (
            <Box sx={{ mb: 1, p: 1, borderRadius: 2, backgroundColor: theme => theme.palette.action.hover }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Pembayaran tertunda
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {pendingPayments.map(payment => (
                  <Button
                    key={payment.orderId}
                    size="small"
                    variant="contained"
                    onClick={() => window.location.href = payment.redirectUrl}
                  >
                    Bayar Sekarang ({payment.orderId})
                  </Button>
                ))}
              </Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 280, overflowY: 'auto', pr: 0.5 }}>
            {items.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
                Belum ada booking aktif.
              </Typography>
            )}
            {items.map((it, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Chip size="small" color={it.type === 'coaching' ? 'secondary' : 'primary'} label={it.type === 'coaching' ? 'Coaching' : 'Mabar'} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                    <ScheduleIcon sx={{ fontSize: 16 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {getHariFromTanggal(it.tanggal)}, {it.tanggal}{it.jamMulai ? ` • ${it.jamMulai}` : ''}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 0.5 }}>
                  <PlaceIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{it.tempat}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.25, gap: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">Atas nama {it.nama}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
                  {hasPendingPayment(it) && (
                    <Button size="small" color="primary" variant="contained" onClick={() => handlePaymentClick(it)}>
                      Bayar Sekarang
                    </Button>
                  )}
                  <Button size="small" color="error" variant="outlined" disabled={!canCancel(it)} onClick={() => setCancelTarget(it)}>
                    Cancel Booking
                  </Button>
                </Box>
              </Paper>
            ))}
          </Box>
          <Dialog open={!!cancelTarget} onClose={() => setCancelTarget(null)}>
            <DialogTitle>Permintaan Pembatalan Booking</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Untuk reschedule atau refund dana silakan hubungi admin!
              </Typography>
              <Box sx={{ p: 2, backgroundColor: 'rgba(33, 150, 243, 0.08)', borderRadius: 1, mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Booking {cancelTarget?.type === 'coaching' ? 'Coaching Clinic' : 'Mabar'}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {cancelTarget?.tempat}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {cancelTarget ? `${getHariFromTanggal(cancelTarget.tanggal)}, ${cancelTarget.tanggal}` : ''}{cancelTarget?.jamMulai ? ` • ${cancelTarget.jamMulai}` : ''}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCancelTarget(null)}>Tutup</Button>
              <Button
                color="error"
                variant="contained"
                onClick={() => {
                  if (!cancelTarget) return;
                  
                  // Generate WhatsApp message
                  const bookingType = cancelTarget.type === 'coaching' ? 'Coaching Clinic' : 'Mabar';
                  const message = `Halo Admin! Saya ingin membatalkan booking saya:\n\nJenis: ${bookingType}\nLokasi: ${cancelTarget.tempat}\nTanggal: ${cancelTarget.tanggal}\nJam: ${cancelTarget.jamMulai || '-'}\nNama: ${cancelTarget.nama}\n\nTerima kasih.`;
                  
                  // Open WhatsApp
                  const encodedMessage = encodeURIComponent(message);
                  const whatsappUrl = `https://wa.me/6283890485939?text=${encodedMessage}`;
                  window.open(whatsappUrl, '_blank');
                  
                  setCancelTarget(null);
                }}
              >
                Hubungi Admin
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      )}
    </Box>
  );
};

export default FloatingBookingStatus;


